import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subject, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

import { ToastService } from '../../../shared/services/toast.service';
import { EmailMessageApiService } from '../../correos/services/email-message-api.service';
import { FileApiService } from '../../correos/services/file-api.service';
import { EmailComposeService, EmailComposeOptions } from '../../correos/services/email-compose.service';
import { SendEmailDto, SendEmailRecipient, SendEmailAttachment } from '../../correos/models/email-message.model';
import { ContactoApiService } from '../../contactos/services/contacto-api.service';
import { Contact } from '../../contactos/models/contacto.model';
import { AuthService } from '../../auth-pages/services/auth.service';

/**
 * Destinatario principal (To) elegido en el compose.
 * - Contacto del CRM: email + idContact + fullName.
 * - Correo escrito a mano: solo email (idContact = null).
 */
interface ComposeRecipient {
  email: string;
  fullName: string | null;
  idContact: number | null;
}

/**
 * Un adjunto en el compose. Mientras `uploading` es true el archivo aún se está
 * subiendo (chip con spinner); cuando termina se completa `fileId` y demás. Si
 * la subida falla, `error` queda en true (chip rojo, no se envía).
 */
interface ComposeAttachment {
  /** Clave local estable para *ngFor track (la subida puede no haber terminado). */
  key: number;
  name: string;
  size: number;
  uploading: boolean;
  error: boolean;
  // Disponibles solo cuando la subida terminó OK:
  fileId?: number;
  originalName?: string;
  contentType?: string;
  fileSize?: number;
}

/** Opción de tipografía para el select de fuente del editor. */
interface FontOption { label: string; value: string; }
/** Opción de tamaño (execCommand fontSize usa 1..7). */
interface FontSizeOption { label: string; value: string; }

const MAX_FILE_BYTES = 10 * 1024 * 1024; // 10 MB

/**
 * Ventana flotante de redacción de correo (tipo Gmail), anclada abajo-derecha y
 * por encima de todo (z-modal). Montada UNA sola vez en el shell
 * (AppLayoutComponent): aparece sobre cualquier pantalla.
 *
 * GLOBAL: se abre/cierra vía EmailComposeService (state$). Puntos de apertura:
 * header, quick-actions de contacto, botón Redactar de la bandeja, botón
 * Responder de un recibido, fila de la lista de contactos. Soporta prefill
 * { email, fullName, contactId, subject, body }, MÚLTIPLES destinatarios (To),
 * CC/CCO, editor de texto enriquecido y adjuntos (subida previa a
 * /api/files/upload). Envía vía POST /api/emails/send (purpose CONTACT_MESSAGE).
 */
@Component({
  selector: 'app-email-compose',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './email-compose.component.html',
  styles: [`
    .email-editor { outline: none; }
    .email-editor:empty::before {
      content: attr(data-placeholder);
      color: #9ca3af;
      pointer-events: none;
    }
    .email-editor blockquote {
      border-left: 3px solid #d1d5db;
      margin: 0.5rem 0;
      padding-left: 0.75rem;
      color: #6b7280;
    }
    .email-editor ul { list-style: disc; padding-left: 1.5rem; }
    .email-editor ol { list-style: decimal; padding-left: 1.5rem; }
    .email-editor a { color: #465fff; text-decoration: underline; }
    .email-editor img { max-width: 100%; height: auto; }
    :host-context(.dark) .email-editor:empty::before { color: #6b7280; }
    :host-context(.dark) .email-editor blockquote { border-left-color: #4b5563; color: #9ca3af; }
  `]
})
export class EmailComposeComponent implements OnInit, OnDestroy {

  /** Editor WYSIWYG (contenteditable). Solo existe mientras la ventana está abierta. */
  @ViewChild('editor') editorRef?: ElementRef<HTMLDivElement>;

  /** <input type=file> oculto para insertar imágenes inline en el cuerpo. */
  @ViewChild('imagenInput') imagenInputRef?: ElementRef<HTMLInputElement>;

  isOpen = false;
  /** Minimizada: la ventana queda como una barra compacta abajo-derecha. */
  minimized = false;
  sending = false;
  errorMessage = '';

  // ── Destinatarios (To) — lista de chips ──
  recipients: ComposeRecipient[] = [];

  // Buscador de contactos / correo libre.
  toQuery = '';
  contactResults: Contact[] = [];
  contactSearching = false;
  showResults = false;
  private readonly contactSearch$ = new Subject<string>();

  // ── CC / CCO (emails libres separados por coma o ;) ──
  ccRaw = '';
  bccRaw = '';
  /** Campos revelables tipo Gmail. */
  showCc = false;
  showBcc = false;

  // ── Asunto ──
  subject = '';

  // ── Contexto de origen (Inc 24): lead/deal desde donde se abrió el compose ──
  private contextEntityType?: string;
  private contextEntityId?: number;

  // ── Adjuntos ──
  attachments: ComposeAttachment[] = [];
  private attachmentKeySeq = 0;

  // ── Editor enriquecido ──
  /** Selección guardada del editor (para reaplicar formato tras usar selects/color). */
  private savedRange: Range | null = null;
  /** Paleta de emojis del dropdown. */
  showEmoji = false;
  /** Subida de imagen inline en curso (deshabilita el botón + spinner). Inc 6. */
  subiendoImagen = false;
  readonly fonts: FontOption[] = [
    { label: 'Sans', value: 'Arial, Helvetica, sans-serif' },
    { label: 'Serif', value: 'Georgia, Times New Roman, serif' },
    { label: 'Mono', value: 'Courier New, monospace' },
    { label: 'Verdana', value: 'Verdana, sans-serif' },
    { label: 'Tahoma', value: 'Tahoma, sans-serif' }
  ];
  readonly fontSizes: FontSizeOption[] = [
    { label: 'Pequeña', value: '2' },
    { label: 'Normal', value: '3' },
    { label: 'Mediana', value: '4' },
    { label: 'Grande', value: '5' },
    { label: 'Enorme', value: '6' }
  ];
  readonly emojis: string[] = [
    '😀', '😁', '😂', '🙂', '😉', '😍', '😘', '😎',
    '🤔', '👍', '👏', '🙏', '🎉', '✅', '❌', '⚠️',
    '📎', '📅', '📞', '✉️', '💡', '🔥', '⭐', '❤️'
  ];

  private readonly subscription = new Subscription();

  constructor(
    private emailApi: EmailMessageApiService,
    private fileApi: FileApiService,
    private contactoApi: ContactoApiService,
    private authService: AuthService,
    private compose: EmailComposeService,
    private toast: ToastService
  ) {}

  ngOnInit(): void {
    this.subscription.add(
      this.contactSearch$.pipe(debounceTime(300)).subscribe(term => this.ejecutarBusquedaContacto(term))
    );
    // Apertura/cierre global controlado por el servicio.
    this.subscription.add(
      this.compose.state$.subscribe(state => {
        if (state.isOpen) {
          this.abrirCon(state.options);
        } else {
          this.isOpen = false;
        }
      })
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  // ── Apertura / cierre ──────────────────────────────────────────────

  private abrirCon(options: EmailComposeOptions): void {
    this.resetForm();
    // Prefill del primer destinatario.
    if (options.email && this.esEmailValido(options.email)) {
      this.recipients = [{
        email: options.email,
        fullName: options.fullName?.trim() || null,
        idContact: options.contactId ?? null
      }];
    }
    if (options.subject) this.subject = options.subject.substring(0, 500);
    this.contextEntityType = options.contextEntityType;
    this.contextEntityId = options.contextEntityId;
    this.isOpen = true;
    this.minimized = false;
    // El editor se renderiza tras aplicar el @if(isOpen): prefill diferido.
    const body = options.body || '';
    setTimeout(() => this.aplicarContenidoEditor(body), 0);
  }

  /** Cierre desde la X / Cancelar → notifica al servicio (que limpia el estado). */
  close(): void {
    if (this.sending) return;
    this.compose.close();
  }

  toggleMinimize(): void {
    this.minimized = !this.minimized;
  }

  private resetForm(): void {
    this.recipients = [];
    this.toQuery = '';
    this.contactResults = [];
    this.contactSearching = false;
    this.showResults = false;
    this.ccRaw = '';
    this.bccRaw = '';
    this.showCc = false;
    this.showBcc = false;
    this.subject = '';
    this.contextEntityType = undefined;
    this.contextEntityId = undefined;
    this.attachments = [];
    this.errorMessage = '';
    this.minimized = false;
    this.showEmoji = false;
    this.subiendoImagen = false;
    this.savedRange = null;
    const el = this.editorRef?.nativeElement;
    if (el) el.innerHTML = '';
  }

  // ── Buscador de destinatario (typeahead contacto / correo libre) ───

  onToQueryChange(): void {
    this.showResults = true;
    this.contactSearch$.next(this.toQuery.trim());
  }

  private ejecutarBusquedaContacto(term: string): void {
    if (term.length < 2) {
      this.contactResults = [];
      this.contactSearching = false;
      return;
    }
    const businessId = this.authService.getCurrentUser()?.businessId;
    if (businessId === undefined || businessId === null) {
      this.contactResults = [];
      return;
    }
    this.contactSearching = true;
    this.contactoApi.searchContacts({ businessId, searchTerm: term, page: 0, size: 8 }).subscribe({
      next: (res) => {
        this.contactResults = res.content;
        this.contactSearching = false;
      },
      error: () => {
        this.contactResults = [];
        this.contactSearching = false;
      }
    });
  }

  seleccionarContacto(c: Contact): void {
    if (!c.mail) {
      this.toast.error({ title: 'Sin correo', message: 'Este contacto no tiene una dirección de correo.' });
      return;
    }
    this.agregarDestinatario({
      email: c.mail,
      fullName: c.fullname?.trim() || null,
      idContact: c.id ?? null
    });
  }

  usarCorreoLibre(): void {
    const email = this.toQuery.trim();
    if (!this.esEmailValido(email)) {
      this.toast.error({ title: 'Correo inválido', message: 'Escribe una dirección de correo válida.' });
      return;
    }
    this.agregarDestinatario({ email, fullName: null, idContact: null });
  }

  /** Añade a la lista de chips (evita duplicados por email) y limpia el input. */
  private agregarDestinatario(r: ComposeRecipient): void {
    const dup = this.recipients.some(x => x.email.toLowerCase() === r.email.toLowerCase());
    if (!dup) this.recipients.push(r);
    this.toQuery = '';
    this.contactResults = [];
    this.showResults = false;
  }

  quitarDestinatario(r: ComposeRecipient): void {
    this.recipients = this.recipients.filter(x => x.email.toLowerCase() !== r.email.toLowerCase());
  }

  /** Etiqueta corta del chip de destinatario. */
  recipientLabel(r: ComposeRecipient): string {
    return r.fullName?.trim() || r.email;
  }

  cerrarResultados(): void {
    this.showResults = false;
    this.contactResults = [];
  }

  // ── Adjuntos ───────────────────────────────────────────────────────

  /** Dispara el <input type=file> oculto. */
  onSeleccionarArchivos(event: Event): void {
    const input = event.target as HTMLInputElement;
    const files = input.files;
    if (!files || files.length === 0) return;
    for (let i = 0; i < files.length; i++) {
      this.subirArchivo(files[i]);
    }
    // Permite volver a elegir el mismo archivo.
    input.value = '';
  }

  private subirArchivo(file: File): void {
    if (file.size > MAX_FILE_BYTES) {
      this.toast.error({
        title: 'Archivo muy grande',
        message: `"${file.name}" supera el límite de 10 MB.`
      });
      return;
    }

    const att: ComposeAttachment = {
      key: this.attachmentKeySeq++,
      name: file.name,
      size: file.size,
      uploading: true,
      error: false
    };
    this.attachments.push(att);

    this.fileApi.uploadFile(file).subscribe({
      next: (res) => {
        att.uploading = false;
        att.fileId = res.fileId;
        att.originalName = res.originalName;
        att.contentType = res.contentType;
        att.fileSize = res.fileSize;
      },
      error: (err) => {
        att.uploading = false;
        att.error = true;
        const msg = err?.error?.message || `No se pudo subir "${file.name}".`;
        this.toast.error({ title: 'Error al adjuntar', message: msg });
      }
    });
  }

  quitarAdjunto(att: ComposeAttachment): void {
    this.attachments = this.attachments.filter(a => a.key !== att.key);
  }

  /** Hay al menos un adjunto subiéndose en este momento. */
  get hayAdjuntosSubiendo(): boolean {
    return this.attachments.some(a => a.uploading);
  }

  /** Tamaño legible para el chip. */
  formatSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  // ── Editor de texto enriquecido (contenteditable + execCommand) ────

  /**
   * Guarda la selección actual si está dentro del editor. Se llama en cada
   * interacción del editor para poder reaplicar formato después de usar un
   * control que roba el foco (select de fuente, input de color, emoji).
   */
  guardarSeleccion(): void {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return;
    const range = sel.getRangeAt(0);
    const el = this.editorRef?.nativeElement;
    if (el && el.contains(range.commonAncestorContainer)) {
      this.savedRange = range.cloneRange();
    }
  }

  /** Reenfoca el editor y restaura la selección guardada. */
  private restaurarSeleccion(): void {
    const el = this.editorRef?.nativeElement;
    if (!el) return;
    el.focus();
    if (this.savedRange) {
      const sel = window.getSelection();
      sel?.removeAllRanges();
      sel?.addRange(this.savedRange);
    }
  }

  /**
   * Ejecuta un comando de formato sobre la selección actual del editor. Los
   * botones de la barra hacen `mousedown.preventDefault` para no perder la
   * selección, por lo que aquí basta con reenfocar.
   */
  exec(cmd: string, value?: string): void {
    const el = this.editorRef?.nativeElement;
    if (el) el.focus();
    document.execCommand(cmd, false, value);
    this.guardarSeleccion();
  }

  aplicarFuente(ev: Event): void {
    const target = ev.target as HTMLSelectElement;
    const value = target.value;
    target.selectedIndex = 0; // vuelve a "Fuente"
    if (!value) return;
    this.restaurarSeleccion();
    document.execCommand('fontName', false, value);
    this.guardarSeleccion();
  }

  aplicarTamano(ev: Event): void {
    const target = ev.target as HTMLSelectElement;
    const value = target.value;
    target.selectedIndex = 0; // vuelve a "Tamaño"
    if (!value) return;
    this.restaurarSeleccion();
    document.execCommand('fontSize', false, value);
    this.guardarSeleccion();
  }

  aplicarColorTexto(ev: Event): void {
    const color = (ev.target as HTMLInputElement).value;
    this.restaurarSeleccion();
    document.execCommand('foreColor', false, color);
    this.guardarSeleccion();
  }

  aplicarResaltado(ev: Event): void {
    const color = (ev.target as HTMLInputElement).value;
    this.restaurarSeleccion();
    // hiliteColor es el estándar; backColor es el fallback de algunos navegadores.
    if (!document.execCommand('hiliteColor', false, color)) {
      document.execCommand('backColor', false, color);
    }
    this.guardarSeleccion();
  }

  aplicarCita(): void {
    this.exec('formatBlock', 'blockquote');
  }

  insertarEnlace(): void {
    const url = window.prompt('URL del enlace:', 'https://');
    if (!url) return;
    this.restaurarSeleccion();
    document.execCommand('createLink', false, url);
    this.guardarSeleccion();
  }

  /**
   * Inserta una imagen en el cuerpo (Inc 6) subiendo un archivo local. Guarda la
   * selección actual del editor (el diálogo de archivo roba el foco) y dispara
   * el <input type=file> oculto; la inserción real ocurre en
   * `onImagenSeleccionada` cuando termina la subida.
   */
  insertarImagen(): void {
    this.guardarSeleccion();
    this.imagenInputRef?.nativeElement.click();
  }

  /**
   * Handler del <input type=file> de imágenes: valida tipo/tamaño, sube como
   * imagen pública (publicUrl) y la inserta inline en la posición guardada.
   */
  onImagenSeleccionada(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files && input.files[0];
    input.value = ''; // permite volver a elegir el mismo archivo
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      this.toast.error({ title: 'Formato no válido', message: 'Selecciona una imagen (JPG, PNG, WEBP, GIF).' });
      return;
    }
    if (file.size > MAX_FILE_BYTES) {
      this.toast.error({ title: 'Imagen muy grande', message: `"${file.name}" supera el límite de 10 MB.` });
      return;
    }

    this.subiendoImagen = true;
    this.fileApi.uploadPublicImage(file).subscribe({
      next: (res) => {
        this.subiendoImagen = false;
        if (!res.publicUrl) {
          this.toast.error({ title: 'Error', message: 'La imagen se subió pero no se obtuvo una URL pública.' });
          return;
        }
        this.restaurarSeleccion();
        document.execCommand('insertImage', false, res.publicUrl);
        this.guardarSeleccion();
      },
      error: (err) => {
        this.subiendoImagen = false;
        const msg = err?.error?.message || `No se pudo subir "${file.name}".`;
        this.toast.error({ title: 'Error al insertar imagen', message: msg });
      }
    });
  }

  toggleEmoji(): void {
    this.guardarSeleccion();
    this.showEmoji = !this.showEmoji;
  }

  insertarEmoji(e: string): void {
    this.restaurarSeleccion();
    document.execCommand('insertText', false, e);
    this.guardarSeleccion();
    this.showEmoji = false;
  }

  /** Quita todo el formato de la selección (o de todo el cuerpo) → texto plano. */
  quitarFormato(): void {
    const el = this.editorRef?.nativeElement;
    if (!el) return;
    el.focus();
    document.execCommand('selectAll', false);
    document.execCommand('removeFormat', false);
    document.execCommand('unlink', false);
    this.guardarSeleccion();
  }

  /** Prefill de texto plano en el editor (o vacío). */
  private aplicarContenidoEditor(text: string): void {
    const el = this.editorRef?.nativeElement;
    if (!el) return;
    if (text) {
      el.innerText = text;
    } else {
      el.innerHTML = '';
    }
  }

  /** Lee el editor: HTML sanitizado + texto plano de fallback. */
  private lecturaEditor(): { html: string; text: string } {
    const el = this.editorRef?.nativeElement;
    if (!el) return { html: '', text: '' };
    const text = (el.innerText || '').trim();
    const rawHtml = (el.innerHTML || '').trim();
    const html = text ? this.sanitizarHtml(rawHtml) : '';
    return { html, text };
  }

  /** Sanea lo peligroso (scripts/handlers) del HTML producido por el editor. */
  private sanitizarHtml(html: string): string {
    return html
      .replace(/<script[\s\S]*?<\/script>/gi, '')
      .replace(/<style[\s\S]*?<\/style>/gi, '')
      .replace(/ on\w+="[^"]*"/gi, '')
      .replace(/ on\w+='[^']*'/gi, '')
      .replace(/javascript:/gi, '');
  }

  // ── Validación ─────────────────────────────────────────────────────

  private esEmailValido(email: string): boolean {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  private parseEmails(raw: string): string[] {
    return raw
      .split(/[,;]/)
      .map(s => s.trim())
      .filter(s => this.esEmailValido(s));
  }

  private parseCc(): string[] {
    return this.parseEmails(this.ccRaw);
  }

  private parseBcc(): string[] {
    return this.parseEmails(this.bccRaw);
  }

  /** true si el botón Enviar está habilitado. */
  get puedeEnviar(): boolean {
    return !this.sending
      && !this.hayAdjuntosSubiendo
      && !this.subiendoImagen
      && this.recipients.length > 0
      && this.recipients.every(r => this.esEmailValido(r.email))
      && this.subject.trim().length > 0;
  }

  get tieneTextoSinConfirmar(): boolean {
    return this.toQuery.trim().length > 0;
  }

  // ── Envío ──────────────────────────────────────────────────────────

  enviar(): void {
    if (!this.puedeEnviar) return;

    const recipients: SendEmailRecipient[] = this.recipients.map(r => this.buildRecipient(r));
    for (const cc of this.parseCc()) {
      recipients.push({ kind: 'CC', email: cc });
    }
    for (const bcc of this.parseBcc()) {
      recipients.push({ kind: 'BCC', email: bcc });
    }

    // idContact principal (link al timeline): primer destinatario con contacto.
    const idContact = this.recipients.find(r => r.idContact !== null)?.idContact ?? undefined;

    const { html, text } = this.lecturaEditor();
    const dto: SendEmailDto = {
      purpose: 'CONTACT_MESSAGE',
      recipients,
      subject: this.subject.trim().substring(0, 500),
      bodyText: text || undefined,
      bodyHtml: html || undefined,
      idContact
    };

    // Inc 24: adjunta el contexto de origen (LEAD/DEAL) para que el correo
    // quede en el timeline de la entidad además de en la bandeja de enviados.
    if (this.contextEntityType && this.contextEntityId !== undefined) {
      dto.contextEntityType = this.contextEntityType;
      dto.contextEntityId = this.contextEntityId;
    }

    const attachments = this.buildAttachments();
    if (attachments.length > 0) dto.attachments = attachments;

    this.sending = true;
    this.errorMessage = '';

    this.emailApi.sendEmail(dto).subscribe({
      next: () => {
        this.sending = false;
        this.toast.success({ title: 'Correo en camino', message: 'El correo se encoló y se enviará en breve.' });
        this.compose.notifySent();
        this.compose.close();
      },
      error: (err) => {
        this.sending = false;
        const msg = err?.error?.message || 'No se pudo enviar el correo. Revisa tu canal de correo conectado.';
        this.errorMessage = msg;
        this.toast.error({ title: 'Error', message: msg });
      }
    });
  }

  /** Adjuntos subidos OK → shape del dto (storageRef = String(fileId)). */
  private buildAttachments(): SendEmailAttachment[] {
    return this.attachments
      .filter(a => !a.uploading && !a.error && a.fileId !== undefined)
      .map(a => ({
        fileName: a.originalName || a.name,
        mimeType: a.contentType || 'application/octet-stream',
        sizeBytes: a.fileSize ?? a.size,
        storageRef: String(a.fileId)
      }));
  }

  private buildRecipient(r: ComposeRecipient): SendEmailRecipient {
    const recipient: SendEmailRecipient = { kind: 'TO', email: r.email };
    if (r.fullName) recipient.fullName = r.fullName;
    if (r.idContact !== null) recipient.idContact = r.idContact;
    return recipient;
  }
}
